import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Devices } from '@app/_models/devices';
import { AccessManagementService } from '@app/_services/access-management.service';
import { DeviceService } from '@app/_services/device.service';
import { faCheckCircle, faTimesCircle, faRotateLeft, faEye, faFilter, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { ToastrService } from 'ngx-toastr';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface ManageableProperty {
  id: number;
  name: string;
  address?: string;
  rolename?: string;
}

interface GuestRequestRecord {
  id: number;
  firstname?: string;
  lastname?: string;
  email?: string;
  approved?: boolean | null;
  startdtime?: string;
  stopdtime?: string;
  numberofusages?: number;
  crontimeconfig?: string;
  devices?: any;
  accesskey?: string;
}

@Component({
  selector: 'app-manage-guest',
  templateUrl: './manage-guest.component.html',
  styleUrls: ['./manage-guest.component.css'],
  standalone: false
})
export class ManageGuestComponent implements OnInit {
  faCheckCircle = faCheckCircle;
  faTimesCircle = faTimesCircle;
  faRotateLeft = faRotateLeft;
  faEye = faEye;
  faFilter = faFilter;
  faSpinner = faSpinner;

  statusFilters = ['All', 'Pending', 'Approved', 'Denied'];
  usageOptions = ['1 time', '2 times', 'Unlimited', 'Custom'];

  manageableProperties = signal<ManageableProperty[]>([]);
  selectedPropertyId = signal<number | null>(null);
  selectedStatusFilter = signal<string>('All');
  propertyDevices = signal<Devices[]>([]);
  guestList = signal<GuestRequestRecord[]>([]);

  isLoadingProperties = signal(false);
  isLoadingGuests = signal(false);
  isLoadingDevices = signal(false);

  propertiesMessage = signal('');
  guestsMessage = signal('');
  devicesMessage = signal('');

  approvalGuestId = signal<number | null>(null);
  selectedDeviceIds = signal<number[]>([]);
  cronEnabled = signal(false);

  approveForm!: FormGroup;

  filteredGuests = computed(() => {
    const selected = this.selectedStatusFilter();
    if (selected === 'All') {
      return this.guestList();
    }

    return this.guestList().filter((guest) => this.approvalStatus(guest.approved) === selected);
  });

  private destroyRef = inject(DestroyRef);

  constructor(
    private accessManagementService: AccessManagementService,
    private deviceService: DeviceService,
    private formBuilder: FormBuilder,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    const now = new Date();
    const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);

    this.approveForm = this.formBuilder.group({
      startdtime: [this.toInputDateTimeLocal(now), Validators.required],
      stopdtime: [this.toInputDateTimeLocal(oneHourLater), Validators.required],
      usageOption: ['1 time', Validators.required],
      customUsage: [''],
      dailyStartTime: ['08:00'],
      dailyStopTime: ['18:00']
    });

    this.loadManageableProperties();
  }

  loadManageableProperties(): void {
    this.isLoadingProperties.set(true);
    this.propertiesMessage.set('');
    this.guestsMessage.set('');
    this.devicesMessage.set('');
    this.guestList.set([]);
    this.propertyDevices.set([]);

    this.accessManagementService.getManageablePropertyRoles()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          const unique = new Map<number, ManageableProperty>();

          (data || []).forEach((entry: any) => {
            const propertyId = Number(entry?.id);
            const roleName = String(entry?.rolename || '').toUpperCase();

            if (!Number.isFinite(propertyId)) {
              return;
            }

            if (roleName !== 'ROLE_ADMIN' && roleName !== 'ADMIN' && roleName !== 'ROLE_SUPER' && roleName !== 'SUPER') {
              return;
            }

            unique.set(propertyId, {
              id: propertyId,
              name: String(entry?.name || `Property ${propertyId}`),
              address: String(entry?.address || ''),
              rolename: String(entry?.rolename || '')
            });
          });

          const properties = Array.from(unique.values()).sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
          this.manageableProperties.set(properties);
          this.isLoadingProperties.set(false);

          if (!properties.length) {
            this.propertiesMessage.set('You do not have any properties under management.');
            this.selectedPropertyId.set(null);
            return;
          }

          const initialPropertyId = properties.length === 1 ? properties[0].id : this.selectedPropertyId();
          if (initialPropertyId) {
            this.onPropertyChanged(initialPropertyId);
          }
        },
        error: () => {
          this.isLoadingProperties.set(false);
          this.propertiesMessage.set('Failed to load manageable properties.');
        }
      });
  }

  onPropertyChanged(propertyId: number): void {
    this.selectedPropertyId.set(propertyId);
    this.approvalGuestId.set(null);
    this.loadPropertyDevices(propertyId);
    this.loadGuestList(propertyId);
  }

  loadPropertyDevices(propertyId: number): void {
    this.isLoadingDevices.set(true);
    this.devicesMessage.set('');
    this.propertyDevices.set([]);

    this.accessManagementService.getGuestPropertyDevices(propertyId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (devices) => {
          const normalized = (devices || []).map((d: any) => ({
            id: Number(d?.id),
            name: d?.name,
            labeltxt: d?.labeltxt,
            relayindex: d?.relayindex,
            enabled: d?.enabled,
            description: d?.description,
            property_id: Number(d?.property_id)
          } as Devices)).filter((d) => Number.isFinite(d.id));

          this.propertyDevices.set(normalized);
          this.isLoadingDevices.set(false);
          if (!normalized.length) {
            this.devicesMessage.set('No devices found for this property.');
          }
        },
        error: () => {
          this.isLoadingDevices.set(false);
          this.devicesMessage.set('Failed to load devices for this property.');
        }
      });
  }

  loadGuestList(propertyId: number): void {
    this.isLoadingGuests.set(true);
    this.guestsMessage.set('');
    this.guestList.set([]);

    this.accessManagementService.viewGuestAccessRequestPropertyId(propertyId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (guests) => {
          const normalized = (guests || []).map((item: any) => ({
            id: Number(item?.id),
            firstname: item?.firstname,
            lastname: item?.lastname,
            email: item?.email,
            approved: item?.approved,
            startdtime: item?.startdtime,
            stopdtime: item?.stopdtime,
            numberofusages: item?.numberofusages,
            crontimeconfig: item?.crontimeconfig,
            devices: item?.devices,
            accesskey: item?.accesskey
          } as GuestRequestRecord)).filter((item: GuestRequestRecord) => Number.isFinite(item.id));

          this.guestList.set(normalized);
          this.isLoadingGuests.set(false);
          if (!normalized.length) {
            this.guestsMessage.set('No guest requests found for this property.');
          }
        },
        error: () => {
          this.isLoadingGuests.set(false);
          this.guestsMessage.set('Failed to load guest requests.');
        }
      });
  }

  setStatusFilter(status: string): void {
    this.selectedStatusFilter.set(status);
  }

  approvalStatus(approved: boolean | null | undefined): string {
    if (approved === true) {
      return 'Approved';
    }

    if (approved === false) {
      return 'Denied';
    }

    return 'Pending';
  }

  isProcessed(approved: boolean | null | undefined): boolean {
    return approved === true || approved === false;
  }

  openApprove(guest: GuestRequestRecord): void {
    const now = new Date();
    const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);

    this.approvalGuestId.set(guest.id);
    this.selectedDeviceIds.set([]);
    this.cronEnabled.set(false);
    this.approveForm.patchValue({
      startdtime: this.toInputDateTimeLocal(now),
      stopdtime: this.toInputDateTimeLocal(oneHourLater),
      usageOption: '1 time',
      customUsage: '',
      dailyStartTime: '08:00',
      dailyStopTime: '18:00'
    });
  }

  cancelApprove(): void {
    this.approvalGuestId.set(null);
    this.selectedDeviceIds.set([]);
    this.cronEnabled.set(false);
  }

  toggleDeviceSelection(deviceId: number, checked: boolean): void {
    const current = [...this.selectedDeviceIds()];
    if (checked && !current.includes(deviceId)) {
      current.push(deviceId);
    }

    if (!checked) {
      const idx = current.indexOf(deviceId);
      if (idx >= 0) {
        current.splice(idx, 1);
      }
    }

    this.selectedDeviceIds.set(current);
  }

  approveGuest(guestId: number): void {
    if (this.approveForm.invalid) {
      this.toastr.info('Please complete the approval form.', 'Information');
      return;
    }

    const startDate = new Date(this.approveForm.value.startdtime);
    const stopDate = new Date(this.approveForm.value.stopdtime);

    if (stopDate.getTime() < startDate.getTime()) {
      this.toastr.error('Stop date/time must be the same or after start date/time.', 'Error');
      return;
    }

    const selectedDeviceIds = this.selectedDeviceIds();
    if (this.propertyDevices().length > 0 && selectedDeviceIds.length === 0) {
      this.toastr.error('Select at least one device/gate.', 'Error');
      return;
    }

    const usageOption = String(this.approveForm.value.usageOption || '1 time');
    let numberOfUsages = 1;

    if (usageOption === '2 times') {
      numberOfUsages = 2;
    } else if (usageOption === 'Unlimited') {
      numberOfUsages = 0;
    } else if (usageOption === 'Custom') {
      const customUsage = Number(this.approveForm.value.customUsage);
      if (!Number.isInteger(customUsage) || customUsage < 1 || customUsage > 20) {
        this.toastr.error('Custom usages must be an integer between 1 and 20.', 'Error');
        return;
      }
      numberOfUsages = customUsage;
    }

    const selectedDevices = this.propertyDevices()
      .filter((device) => selectedDeviceIds.includes(Number(device.id)))
      .map((device) => ({
        id: Number(device.id),
        labeltxt: String(device.labeltxt || '')
      }))
      .sort((a, b) => a.id - b.id);

    const durationHours = Math.floor((stopDate.getTime() - startDate.getTime()) / 3600000);
    const cronTimeConfig = this.buildCronTimeConfig(durationHours);

    const payload: any = {
      approved: true,
      startdtime: this.formatApiDateTimeUtc(startDate),
      stopdtime: this.formatApiDateTimeUtc(stopDate),
      devices: JSON.stringify(selectedDevices),
      numberofusages: numberOfUsages
    };

    if (cronTimeConfig) {
      payload.crontimeconfig = cronTimeConfig;
    }

    this.accessManagementService.approveOrDenyGuestRequest(guestId, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp) => {
          this.toastr.success(resp?.message || 'Guest request approved successfully.', 'Success');
          this.cancelApprove();
          if (this.selectedPropertyId()) {
            this.loadGuestList(this.selectedPropertyId()!);
          }
        },
        error: (error) => {
          this.toastr.error(error?.error?.message || 'Failed to approve guest request.', 'Error');
        }
      });
  }

  denyGuestNow(guestId: number): void {
    const now = new Date();

    const payload = {
      approved: false,
      startdtime: this.formatApiDateTimeUtc(now),
      stopdtime: this.formatApiDateTimeUtc(now)
    };

    this.accessManagementService.approveOrDenyGuestRequest(guestId, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp) => {
          this.toastr.success(resp?.message || 'Guest request denied successfully.', 'Success');
          if (this.selectedPropertyId()) {
            this.loadGuestList(this.selectedPropertyId()!);
          }
        },
        error: (error) => {
          this.toastr.error(error?.error?.message || 'Failed to deny guest request.', 'Error');
        }
      });
  }

  revokeGuestAccess(guest: GuestRequestRecord): void {
    const stopdtime = new Date(Date.now() - 2 * 60 * 1000);

    this.accessManagementService.revokeGuestAccess(guest.id, {
      stopdtime: this.formatApiDateTimeUtc(stopdtime)
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp) => {
          this.toastr.success(resp?.message || 'Guest access revoked successfully.', 'Success');
          if (this.selectedPropertyId()) {
            this.loadGuestList(this.selectedPropertyId()!);
          }
        },
        error: (error) => {
          this.toastr.error(error?.error?.message || 'Failed to revoke guest access.', 'Error');
        }
      });
  }

  canRevokeGuest(stopDateTimeValue: string | undefined): boolean {
    if (!stopDateTimeValue) {
      return false;
    }

    const stopDateTime = this.parseApiDateTime(stopDateTimeValue);
    if (!stopDateTime) {
      return false;
    }

    return stopDateTime.getTime() > Date.now();
  }

  formatApiDateTimeForDisplayLocal(value: string | undefined): string {
    if (!value) {
      return '-';
    }

    const parsed = this.parseApiDateTime(value);
    if (!parsed) {
      return value;
    }

    return parsed.toLocaleString();
  }

  formatGuestDevicesForDisplay(devicesValue: any): string {
    const labels = new Set<string>();

    if (Array.isArray(devicesValue)) {
      devicesValue.forEach((entry) => {
        const label = String(entry?.labeltxt || entry?.labelTxt || '').trim();
        if (label) {
          labels.add(label);
        }
      });
    } else if (typeof devicesValue === 'string') {
      const raw = devicesValue.trim();
      if (!raw) {
        return '-';
      }

      try {
        const decoded = JSON.parse(raw);
        if (Array.isArray(decoded)) {
          decoded.forEach((entry) => {
            const label = String(entry?.labeltxt || entry?.labelTxt || '').trim();
            if (label) {
              labels.add(label);
            }
          });
        }
      } catch {
        return '-';
      }
    }

    return labels.size ? Array.from(labels).join(', ') : '-';
  }

  private buildCronTimeConfig(durationHours: number): string | undefined {
    if (durationHours <= 48) {
      return undefined;
    }

    if (!this.cronEnabled()) {
      return undefined;
    }

    const startTime = String(this.approveForm.value.dailyStartTime || '').trim();
    const stopTime = String(this.approveForm.value.dailyStopTime || '').trim();
    if (!startTime || !stopTime || !startTime.includes(':') || !stopTime.includes(':')) {
      return undefined;
    }

    const [startHour, startMinute] = startTime.split(':').map((x: string) => Number(x));
    const [stopHour, stopMinute] = stopTime.split(':').map((x: string) => Number(x));

    if (![startHour, startMinute, stopHour, stopMinute].every((x) => Number.isInteger(x))) {
      return undefined;
    }

    if (startHour === stopHour && startMinute === stopMinute) {
      return undefined;
    }

    return `* ${startHour}|${startMinute}-${stopHour}|${stopMinute} * * *`;
  }

  private toInputDateTimeLocal(date: Date): string {
    const tzOffset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);
  }

  private formatApiDateTimeUtc(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');

    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`;
  }

  private parseApiDateTime(value: string): Date | null {
    const raw = value.trim();
    if (!raw) {
      return null;
    }

    const hasExplicitTimezone = raw.endsWith('Z') || /[+-]\d\d:\d\d$/.test(raw);

    if (hasExplicitTimezone) {
      const parsed = new Date(raw);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const normalized = raw.includes(' ') ? raw.replace(' ', 'T') : raw;
    const parsed = new Date(`${normalized}Z`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
}
